import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../auth/apiClient';
import SaktiFaceRegistryApiService from '../services/saktiFaceRegistryApiService';
import {
  SaktiFaceEmployeeItem,
  SaktiFaceEmployeeImagesData,
  SaktiFaceEmployeeListData,
  SaktiFaceRegistryFilters,
  SaktiFaceStatsData,
  InspeksiEstateOption,
  InspeksiDivisionOption,
} from '../types';

const DEFAULT_FILTERS: SaktiFaceRegistryFilters = {
  search: '',
  enrolledOnly: false,
  page: 1,
  limit: 50,
  jobPosition: undefined,
};

export function useSaktiFaceRegistry() {
  const [filters, setFilters] = useState<SaktiFaceRegistryFilters>(DEFAULT_FILTERS);
  const [estates, setEstates] = useState<InspeksiEstateOption[]>([]);
  const [allDivisions, setAllDivisions] = useState<InspeksiDivisionOption[]>([]);
  const [jobPositions, setJobPositions] = useState<string[]>([]);
  const [employeeList, setEmployeeList] = useState<SaktiFaceEmployeeListData | null>(null);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [employeesError, setEmployeesError] = useState<string | null>(null);

  const [selectedEmployee, setSelectedEmployee] = useState<SaktiFaceEmployeeItem | null>(null);
  const [employeeImages, setEmployeeImages] = useState<SaktiFaceEmployeeImagesData | null>(null);
  const [loadingImages, setLoadingImages] = useState(false);
  const [imagesError, setImagesError] = useState<string | null>(null);

  const [stats, setStats] = useState<SaktiFaceStatsData | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setDebouncedSearch(filters.search), 400);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [filters.search]);

  const fetchEmployees = useCallback(async () => {
    setLoadingEmployees(true);
    setEmployeesError(null);
    try {
      const data = await SaktiFaceRegistryApiService.getEmployees({
        ...filters,
        search: debouncedSearch,
      });
      setEmployeeList(data);
    } catch {
      setEmployeesError('Gagal memuat daftar karyawan');
    } finally {
      setLoadingEmployees(false);
    }
  }, [filters, debouncedSearch]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  useEffect(() => {
    api.get<{ estates: InspeksiEstateOption[]; divisis: InspeksiDivisionOption[] }>('/inspeksi/options')
      .then((res) => { setEstates(res.data.estates); setAllDivisions(res.data.divisis); })
      .catch(() => {});

    SaktiFaceRegistryApiService.getJobPositions()
      .then(setJobPositions)
      .catch(() => {});
  }, []);

  const fetchStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      setStats(await SaktiFaceRegistryApiService.getStats());
    } catch {
      // Stats are non-critical; fail silently
    } finally {
      setLoadingStats(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const selectEmployee = useCallback(async (employee: SaktiFaceEmployeeItem) => {
    setSelectedEmployee(employee);
    setEmployeeImages(null);
    setImagesError(null);
    setLoadingImages(true);
    try {
      setEmployeeImages(await SaktiFaceRegistryApiService.getEmployeeImages(employee.employee_id));
    } catch {
      setImagesError('Gagal memuat foto karyawan');
    } finally {
      setLoadingImages(false);
    }
  }, []);

  const clearSelectedEmployee = useCallback(() => {
    setSelectedEmployee(null);
    setEmployeeImages(null);
    setImagesError(null);
  }, []);

  const setSearch = useCallback(
    (search: string) => setFilters((prev) => ({ ...prev, search, page: 1 })),
    []
  );
  const setEstateOdooId = useCallback(
    (estateOdooId: number | undefined) =>
      setFilters((prev) => ({ ...prev, estateOdooId, divisionOdooId: undefined, page: 1 })),
    []
  );
  const setDivisionOdooId = useCallback(
    (divisionOdooId: number | undefined) => setFilters((prev) => ({ ...prev, divisionOdooId, page: 1 })),
    []
  );
  const setJobPosition = useCallback(
    (jobPosition: string | undefined) => setFilters((prev) => ({ ...prev, jobPosition, page: 1 })),
    []
  );
  const setEnrolledOnly = useCallback(
    (enrolledOnly: boolean) => setFilters((prev) => ({ ...prev, enrolledOnly, page: 1 })),
    []
  );
  const goToPage = useCallback(
    (page: number) => setFilters((prev) => ({ ...prev, page })),
    []
  );

  // Filter divisions to those belonging to the selected estate (by inspeksi internal estate.id).
  const selectedInspeksiEstate = filters.estateOdooId
    ? estates.find((e) => e.odoo_id === filters.estateOdooId)
    : undefined;
  const divisions = selectedInspeksiEstate
    ? allDivisions.filter((d) => d.estate_id === selectedInspeksiEstate.id)
    : allDivisions;

  return {
    filters,
    estates,
    divisions,
    allDivisions,
    jobPositions,
    employeeList,
    loadingEmployees,
    employeesError,
    stats,
    loadingStats,
    selectedEmployee,
    employeeImages,
    loadingImages,
    imagesError,
    setSearch,
    setEstateOdooId,
    setDivisionOdooId,
    setJobPosition,
    setEnrolledOnly,
    goToPage,
    selectEmployee,
    clearSelectedEmployee,
    reloadEmployees: fetchEmployees,
  };
}
